import type { FieldDefinition } from '@ayumia/contracts';
import type { QueryEnvelope } from '../api/client';
import { text, type LocalizedText } from '../app/i18n';
import { EvidenceInput } from './EvidenceInput';
import { useLoad } from './useLoad';
import { useAppServices } from './ViewContext';
import { ClearableInput } from '../design-system/fields';

/**
 * Saisie d'un champ déclaré (charge utile d'une commande, formulaire de gabarit ; ADR-064 §3, §5).
 * Le type du champ choisit le contrôle ; une référence se choisit dans la liste gouvernée de son
 * type, jamais par un identifiant tapé ; une preuve se téléverse. Aucune validation qui compte ici :
 * le serveur valide la commande ou le formulaire.
 */

export interface InputField {
  field_id: string;
  label?: LocalizedText;
  type: string;
  unit?: string;
  required?: boolean;
  enum_values?: readonly string[];
  enum_labels?: Readonly<Record<string, LocalizedText>>;
  reference_type?: string;
  max_length?: number;
  pattern?: string;
}

/** Champ d'une charge utile ou d'un gabarit (`field-definition`) vers le contrôle. */
export function inputFieldOf(field: FieldDefinition, required = field.required === true): InputField {
  const out: InputField = { field_id: field.field_id, type: field.type, required };
  if (field.label !== undefined) out.label = field.label as LocalizedText;
  if (field.unit !== undefined) out.unit = field.unit;
  if (field.reference_type !== undefined) out.reference_type = field.reference_type;
  if (field.validation?.enum_values !== undefined) out.enum_values = field.validation.enum_values;
  if (field.validation?.enum_labels !== undefined) out.enum_labels = field.validation.enum_labels as Record<string, LocalizedText>;
  if (field.validation?.max_length !== undefined) out.max_length = field.validation.max_length;
  if (field.validation?.pattern !== undefined) out.pattern = field.validation.pattern;
  return out;
}

type Row = Record<string, unknown> & { id?: string };

/** Libellé d'un enregistrement cité : clé lisible plutôt qu'identifiant. */
function recordLabel(row: Row): string {
  for (const key of ['display_name', 'name', 'code', 'label', 'lot_code', 'reference']) {
    const value = row[key];
    if (typeof value === 'string' && value !== '') return value;
    if (typeof value === 'object' && value !== null) return text(value as LocalizedText, 'fr', String(row.id ?? ''));
  }
  return String(row.id ?? '');
}

function ReferencePicker({ id, type, value, onChange, disabled, multiple }: { id: string; type: string; value: unknown; onChange: (value: unknown) => void; disabled: boolean; multiple: boolean }) {
  const services = useAppServices();
  const [rows] = useLoad<QueryEnvelope<Row[]>>(`reference:${type}`, () => services.client.listObjects<Row>(type, { limit: 100 }));
  if (rows.status === 'loading') return <select id={id} disabled aria-busy="true"><option>Chargement…</option></select>;
  if (rows.status === 'error') return <span className="ay-field__error">{rows.error.message}</span>;
  const options = rows.value.data.filter((row) => typeof row.id === 'string');
  if (multiple) {
    const selected = Array.isArray(value) ? value.map(String) : [];
    return (
      <select id={id} multiple disabled={disabled} value={selected} onChange={(event) => onChange([...event.target.selectedOptions].map((option) => option.value))}>
        {options.map((row) => (
          <option key={row.id} value={row.id}>
            {recordLabel(row)}
          </option>
        ))}
      </select>
    );
  }
  return (
    <select id={id} disabled={disabled} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}>
      <option value="">—</option>
      {options.map((row) => (
        <option key={row.id} value={row.id}>
          {recordLabel(row)}
        </option>
      ))}
    </select>
  );
}

function toLocalInput(value: unknown): string {
  if (typeof value !== 'string') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function FieldInput({
  field,
  value,
  onChange,
  disabled = false,
  error,
  subject,
}: {
  field: InputField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  error?: string;
  /** Objet auquel une preuve téléversée se rattache. */
  subject?: { type: string; id: string };
}) {
  const id = `input-${field.field_id}`;
  const described = error === undefined ? undefined : `error-${field.field_id}`;
  const common = { id, disabled, required: field.required === true, 'aria-invalid': error !== undefined, 'aria-describedby': described };
  let control;
  if (field.type === 'enumeration' || field.type === 'boolean') {
    const options = field.type === 'boolean' ? ['true', 'false'] : [...(field.enum_values ?? [])];
    control = (
      <select {...common} value={value === null || value === undefined ? '' : String(value)} onChange={(event) => onChange(event.target.value === '' ? null : field.type === 'boolean' ? event.target.value === 'true' : event.target.value)}>
        <option value="">—</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {field.type === 'boolean' ? (option === 'true' ? 'Oui' : 'Non') : text(field.enum_labels?.[option], 'fr', option)}
          </option>
        ))}
      </select>
    );
  } else if (field.type === 'quantity') {
    const quantity = (value ?? null) as { value?: number; unit?: string } | null;
    control = (
      <span className="ay-field__control">
        <input {...common} type="number" step="any" inputMode="decimal" value={quantity?.value ?? ''} onChange={(event) => onChange(event.target.value === '' ? null : { value: Number(event.target.value), unit: quantity?.unit ?? field.unit ?? '' })} />
        <span className="ay-field__unit">{quantity?.unit ?? field.unit}</span>
      </span>
    );
  } else if (field.type === 'number' || field.type === 'decimal' || field.type === 'integer') {
    const input = <input {...common} type="number" step={field.type === 'integer' ? 1 : 'any'} inputMode="decimal" value={typeof value === 'number' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))} />;
    // Unité accolée au nombre, dans la même case (« 4,2 °C ») : on lit la valeur et son unité d'un coup d'œil.
    control =
      field.unit === undefined || field.unit === '' ? (
        input
      ) : (
        <span className="ay-field__suffixed">
          {input}
          <span className="ay-field__suffix" aria-hidden="true">
            {field.unit}
          </span>
        </span>
      );
  } else if (field.type === 'date') {
    control = <input {...common} type="date" value={typeof value === 'string' ? value.slice(0, 10) : ''} onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)} />;
  } else if (field.type === 'datetime') {
    control = <input {...common} type="datetime-local" value={toLocalInput(value)} onChange={(event) => onChange(event.target.value === '' ? null : new Date(event.target.value).toISOString())} />;
  } else if ((field.type === 'reference' || field.type === 'collection') && field.reference_type === 'Evidence') {
    control = <EvidenceInput id={id} value={Array.isArray(value) ? value.map(String) : typeof value === 'string' ? [value] : []} onChange={(ids) => onChange(field.type === 'collection' ? ids : (ids[0] ?? null))} disabled={disabled} subject={subject} />;
  } else if ((field.type === 'reference' || field.type === 'collection') && field.reference_type !== undefined) {
    control = <ReferencePicker id={id} type={field.reference_type} value={value} onChange={onChange} disabled={disabled} multiple={field.type === 'collection'} />;
  } else if ((field.max_length ?? 0) > 200 || field.type === 'text' || field.type === 'rich_text') {
    control = <textarea {...common} rows={3} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)} />;
  } else {
    control = <ClearableInput {...common} type="text" value={typeof value === 'string' ? value : ''} onValueChange={(next) => onChange(next === '' ? null : next)} />;
  }
  return (
    <div className="ay-field">
      <label className="ay-field__label" htmlFor={id}>
        {text(field.label, 'fr', field.field_id)}
        {field.required === true && <span aria-hidden="true"> *</span>}
      </label>
      {control}
      {error !== undefined && (
        <span id={described} className="ay-field__error">
          {error}
        </span>
      )}
    </div>
  );
}
